import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { CheckCircle, AlertCircle, Eye, EyeOff, Lock, Loader2 } from 'lucide-react';
import PasswordStrengthMeter, { getPasswordPolicyError } from '../components/PasswordStrengthMeter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ActivateAccountPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [userData, setUserData] = useState(null);
  const [tokenError, setTokenError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setLoading(false);
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/verify-activation-token?token=${encodeURIComponent(token)}`);
      const data = await response.json();
      
      if (response.ok && data.valid) {
        setTokenValid(true);
        setUserData(data.user);
      } else {
        setTokenValid(false);
        setTokenError(data.error || '');
      }
    } catch (error) {
      console.error('Token verification error:', error);
      setTokenValid(false);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.password) {
      newErrors.password = 'Le mot de passe est requis';
    } else {
      const policyError = getPasswordPolicyError(formData.password);
      if (policyError) newErrors.password = policyError;
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setActivating(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/activate-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: token,
          new_password: formData.password,
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Compte activé avec succès !');
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              message: 'Votre compte a été activé. Vous pouvez maintenant vous connecter.',
              email: userData?.email 
            } 
          });
        }, 1500);
      } else {
        toast.error(data.detail || 'Erreur lors de l\'activation');
      }
    } catch (error) {
      toast.error('Erreur de connexion au serveur');
    } finally {
      setActivating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-indigo-600" />
            <p className="text-gray-600">Vérification du lien d'activation...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <CardTitle className="text-red-600">Lien invalide ou expiré</CardTitle>
            <CardDescription>
              {tokenError || "Ce lien d'activation n'est plus valide. Il a peut-être expiré ou a déjà été utilisé."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600 text-center">
              Si vous avez besoin d'un nouveau lien d'activation, contactez votre administrateur.
            </p>
            <Button 
              className="w-full" 
              onClick={() => navigate('/login')}
            >
              Retour à la connexion
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50 p-4">
      <Card className="w-full max-w-md" data-testid="activate-account-card">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="h-16 w-16 bg-indigo-100 rounded-full flex items-center justify-center">
              <Lock className="h-8 w-8 text-indigo-600" />
            </div>
          </div>
          <CardTitle>Activez votre compte</CardTitle>
          <CardDescription>
            Bienvenue {userData?.first_name} ! Définissez votre mot de passe pour accéder à la plateforme.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={userData?.email || ''}
                disabled
                className="bg-gray-50"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Nouveau mot de passe</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="••••••••"
                  className={errors.password ? 'border-red-500' : ''}
                  data-testid="activation-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.password && (
                <p className="text-sm text-red-500">{errors.password}</p>
              )}
              <PasswordStrengthMeter password={formData.password} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirm ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  placeholder="••••••••"
                  className={errors.confirmPassword ? 'border-red-500' : ''}
                  data-testid="activation-confirm-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="text-sm text-red-500">{errors.confirmPassword}</p>
              )}
            </div>

            <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-700">
              <p className="font-medium mb-1">Exigences du mot de passe :</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>Au moins 12 caractères</li>
                <li>Une majuscule, une minuscule, un chiffre et un caractère spécial</li>
              </ul>
            </div>

            <Button 
              type="submit" 
              className="w-full" 
              disabled={activating}
              data-testid="activate-account-btn"
            >
              {activating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Activation en cours...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Activer mon compte
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
